import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import ConfirmDialog from '../ConfirmDialog';
import { FileText, Home, Tag, Edit3, X, Plus } from 'lucide-react';
import { toast } from 'sonner';
import { PROPERTY_TYPES } from '../../lib/docTypes';
import { API } from './utils';

export default function PropertyBasicInfo({ property, propertyId, onPropertyUpdated }) {
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editFormData, setEditFormData] = useState(property || {});
  const [customFieldDialogOpen, setCustomFieldDialogOpen] = useState(false);
  const [customFieldDraft, setCustomFieldDraft] = useState({ id: null, label: '', value: '' });
  const [customFieldToDelete, setCustomFieldToDelete] = useState(null);

  // Keep the edit form in sync when the property reloads (e.g. after save).
  useEffect(() => {
    if (property) setEditFormData(property);
  }, [property]);

  const handleEditProperty = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      // Edit Property dialog only mutates Property-Information-card fields.
      // Send a merged payload so we don't clobber tenant/utility data on the server.
      const payload = {
        ...property,
        address: editFormData.address,
        property_type: editFormData.property_type || property.property_type || 'AST',
      };
      await axios.put(`${API}/properties/${propertyId}`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Property updated successfully');
      setEditDialogOpen(false);
      await onPropertyUpdated();
    } catch (error) {
      toast.error('Failed to update property');
    }
  };

  const persistCustomFields = async (fields) => {
    try {
      const token = localStorage.getItem('token');
      const payload = { ...property, custom_fields: fields };
      await axios.put(`${API}/properties/${propertyId}`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      await onPropertyUpdated();
    } catch (error) {
      toast.error('Failed to save custom field');
    }
  };

  const openAddCustomField = () => {
    setCustomFieldDraft({ id: null, label: '', value: '' });
    setCustomFieldDialogOpen(true);
  };

  const openEditCustomField = (field) => {
    setCustomFieldDraft({ ...field });
    setCustomFieldDialogOpen(true);
  };

  const saveCustomField = async (e) => {
    e?.preventDefault?.();
    const label = customFieldDraft.label.trim();
    const value = customFieldDraft.value.trim();
    if (!label || !value) {
      toast.error('Both label and value are required');
      return;
    }
    const existing = property.custom_fields || [];
    let next;
    if (customFieldDraft.id) {
      next = existing.map(f => f.id === customFieldDraft.id ? { ...f, label, value } : f);
    } else {
      next = [...existing, { id: `cf_${Date.now()}_${Math.random().toString(36).slice(2,7)}`, label, value }];
    }
    await persistCustomFields(next);
    setCustomFieldDialogOpen(false);
    toast.success(customFieldDraft.id ? 'Custom field updated' : 'Custom field added');
  };

  const confirmDeleteCustomField = async () => {
    if (!customFieldToDelete) return;
    const next = (property.custom_fields || []).filter(f => f.id !== customFieldToDelete.id);
    await persistCustomFields(next);
    setCustomFieldToDelete(null);
    toast.success('Custom field deleted');
  };

  return (
    <>
      <div className="property-info-section" data-testid="property-info-card">
        <div className="section-header-with-action">
          <h3 className="section-title-simple">
            <FileText size={20} />
            Property Information
          </h3>
          <Button
            onClick={() => setEditDialogOpen(true)}
            className="btn-secondary"
            data-testid="edit-property-button"
          >
            <Edit3 size={18} />
            Edit Property
          </Button>
        </div>
        <div className="property-info-grid">
          <div className="property-info-item">
            <div className="property-info-icon">
              <FileText size={20} />
            </div>
            <div className="property-info-content">
              <div className="property-info-label">Full Address</div>
              <div className="property-info-value" data-testid="property-address-value">{property.address}</div>
            </div>
          </div>
          <div className="property-info-item">
            <div className="property-info-icon">
              <Home size={20} />
            </div>
            <div className="property-info-content">
              <div className="property-info-label">Property Type</div>
              <div className="property-info-value" data-testid="property-type-value">{property.property_type || 'AST'}</div>
            </div>
          </div>
          {(property.custom_fields || []).map((field) => (
            <div key={field.id} className="property-info-item custom-field-item" data-testid={`custom-field-${field.id}`}>
              <div className="property-info-icon">
                <Tag size={20} />
              </div>
              <div className="property-info-content">
                <div className="property-info-label">{field.label}</div>
                <div className="property-info-value">{field.value}</div>
              </div>
              <div className="custom-field-actions">
                <button
                  type="button"
                  onClick={() => openEditCustomField(field)}
                  className="custom-field-btn"
                  title="Edit"
                  data-testid={`edit-custom-field-${field.id}`}
                >
                  <Edit3 size={14} />
                </button>
                <button
                  type="button"
                  onClick={() => setCustomFieldToDelete(field)}
                  className="custom-field-btn custom-field-btn-danger"
                  title="Delete"
                  data-testid={`delete-custom-field-${field.id}`}
                >
                  <X size={14} />
                </button>
              </div>
            </div>
          ))}
        </div>
        <div className="property-info-footer">
          <Button
            onClick={openAddCustomField}
            className="btn-secondary btn-add-custom-field"
            data-testid="add-custom-field-button"
          >
            <Plus size={16} />
            Add Custom Field
          </Button>
        </div>
      </div>

      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="edit-property-dialog">
          <DialogHeader>
            <DialogTitle>Edit Property</DialogTitle>
            <DialogDescription>
              Update the property address or its type. Switching to HMO adds extra HMO documents to your sidebar.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleEditProperty} className="property-form">
            <div className="form-group">
              <Label htmlFor="edit-address">Full Address</Label>
              <Input
                id="edit-address"
                value={editFormData.address || ''}
                onChange={(e) => setEditFormData({ ...editFormData, address: e.target.value })}
                required
                data-testid="edit-property-address-input"
              />
            </div>
            <div className="form-group">
              <Label htmlFor="edit-property-type">Property Type</Label>
              <Select
                value={editFormData.property_type || property.property_type || 'AST'}
                onValueChange={(value) => setEditFormData({ ...editFormData, property_type: value })}
              >
                <SelectTrigger id="edit-property-type" data-testid="edit-property-type-trigger">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {PROPERTY_TYPES.map((p) => (
                    <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="form-hint">Switching to HMO will add the HMO Additional documents to this property.</p>
            </div>
            <Button type="submit" className="btn-primary w-full" data-testid="edit-property-save">
              Update Property
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={customFieldDialogOpen} onOpenChange={setCustomFieldDialogOpen}>
        <DialogContent className="custom-field-dialog">
          <DialogHeader>
            <DialogTitle>{customFieldDraft.id ? 'Edit Custom Field' : 'Add Custom Field'}</DialogTitle>
            <DialogDescription>
              Track any extra information unique to this property — e.g. parking bay number, key safe code, or council tax band.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={saveCustomField} className="custom-field-form">
            <div className="form-group">
              <Label htmlFor="cf-label">Label</Label>
              <Input
                id="cf-label"
                value={customFieldDraft.label}
                placeholder="e.g. Parking Space, Key Safe Code"
                onChange={(e) => setCustomFieldDraft({ ...customFieldDraft, label: e.target.value })}
                data-testid="custom-field-label-input"
                autoFocus
              />
            </div>
            <div className="form-group">
              <Label htmlFor="cf-value">Value</Label>
              <Input
                id="cf-value"
                value={customFieldDraft.value}
                placeholder="e.g. Bay 4, 1234, Band D"
                onChange={(e) => setCustomFieldDraft({ ...customFieldDraft, value: e.target.value })}
                data-testid="custom-field-value-input"
              />
            </div>
            <div className="custom-field-form-actions">
              <Button type="button" onClick={() => setCustomFieldDialogOpen(false)} className="btn-secondary">
                Cancel
              </Button>
              <Button type="submit" className="btn-primary" data-testid="custom-field-save-button">
                {customFieldDraft.id ? 'Save Changes' : 'Add Field'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={!!customFieldToDelete}
        onOpenChange={(open) => { if (!open) setCustomFieldToDelete(null); }}
        title="Delete Custom Field"
        description={
          customFieldToDelete
            ? `Are you sure you want to delete the custom field "${customFieldToDelete.label}"? This action cannot be undone.`
            : ''
        }
        onConfirm={confirmDeleteCustomField}
        confirmText="Delete"
      />
    </>
  );
}
